<template>
  <div class="d-f fd-c ai-c w-100 m-b-children">
    <div class="w-100 bc-black p bs-bb" v-if="labelData && labelData.demoCriteria">
      <!-- <p class="tt-up as-c mute m-0 font-small">Criteria & Assessment</p> -->
      <p class="b-0 ws-pre-wrap mute">{{ labelData.demoCriteria }}</p>
    </div>

    <AudioUploadZone
      :value="songSelected"
      :max-size-mb="90"
      :open-file-picker="getOpenFilePicker"
      :pre-compressed="preCompressed"
      @input="$emit('setSongSelected', $event)"
      @original-file="$emit('original-file', $event)"
      @opened="onAudioUploadOpened"
    />

    <div class="input-container w-100" :class="{ 'field-required': isFileReady && !title }">
      <label class="label" for="title" :class="{ error: isFileReady && !title }">Title</label>
      <input
        id="title"
        v-model="title"
        name="title"
        placeholder="Title"
        type="text"
        :class="{ error: isFileReady && !title }"
      />
    </div>

    <div class="w-100" :class="{ 'field-required': isFileReady && !hasGenreSelected }">
      <GenreSelect
        :value="genreSelected"
        :standalone="false"
        :show-save-buttons="false"
        @input="genreSelected = $event"
      />
      <p v-if="!hasGenreSelected && songSelected" class="mute font-small m-t-sm m-0">
        Please select a genre to continue
      </p>
    </div>

    <div class="input-container w-100">
      <label class="label" for="description">Description</label>
      <textarea
        id="description"
        name="description"
        v-model="description"
        rows="3"
        placeholder="Description"
      />
    </div>

    <p v-if="showSubmitBtn" class="m-t">
      {{ saving ? "Submitting Your Demo..." : "&nbsp;" }}
    </p>
    <div class="save-buttons d-f jc-c ai-c p-f b-0 bc-brand w-100 m-b-0 p">
      <button
        v-if="showSubmitBtn"
        class="secondary d-f ai-c jc-c g"
        :disabled="!submitBtnEnabled"
        @click.prevent="handleSubmitDemo"
      >
        Send demo
        <IconBase w="15" h="15" viewBox="0 0 15 15" icon-name="submit">
          <IconSend />
        </IconBase>
      </button>
      <button
        v-else
        class="secondary d-f ai-c jc-c g"
        :disabled="!submitBtnEnabled"
        @click.prevent="handleSubmitDemo"
      >
        Next step
        <IconBase w="15" h="15" viewBox="0 0 15 15" icon-name="submit">
          <IconArrowRight />
        </IconBase>
      </button>
    </div>
  </div>
</template>

<script>
import IconBase from "@/assets/icons/IconBase.vue";
import IconSend from "@/assets/icons/IconSend.vue";
import { mapState, mapGetters } from "vuex";
import IconArrowRight from "@/assets/icons/IconArrowRight.vue";
import AudioUploadZone from "@/components/mymusic/AudioUploadZone.vue";
import GenreSelect from "@/components/settings/GenreSelect.vue";

export default {
  name: "SubmitDemoTabInfo",
  components: { IconArrowRight, IconSend, IconBase, AudioUploadZone, GenreSelect },
  props: {
    songSelected: {
      type: File,
    },
    labelData: {
      type: Object,
    },
    saving: {
      type: Boolean,
    },
    showSubmitBtn: {
      type: Boolean,
      default: true,
    },
    initialData: {
      type: Object,
    },
    preCompressed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      title: this.initialData?.title ?? "",
      genreSelected: this.initialData?.genreSelected ?? [],
      description: this.initialData?.description ?? "",
      loadingDotsInterval: null,
    };
  },
  beforeDestroy() {
    if (this.loadingDotsInterval) {
      clearInterval(this.loadingDotsInterval);
    }
  },
  methods: {
    handleSubmitDemo() {
      const formData = {
        title: this.title,
        genreSelected: this.genreSelected,
        description: this.description,
      };
      console.log("Submitting form data:", formData);
      console.log("Title:", this.title, "Genre:", this.genreSelected);
      // Emit saveFormData to parent before submitting
      this.$emit("saveFormData", formData);
      this.$emit("submitDemo", formData);
    },
    cleanFileName(fileName) {
      if (!fileName) return "";
      // Remove extension
      const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
      // Remove dashes, underscores, and clean up
      return nameWithoutExt
        .replace(/[-_]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },
    onAudioUploadOpened() {
      this.$store.commit("SET_OPEN_FILE_PICKER", false);
    },
    saveFormData() {
      // Emit form data to parent to save it
      this.$emit("saveFormData", {
        title: this.title,
        genreSelected: this.genreSelected,
        description: this.description,
      });
    },
  },
  computed: {
    ...mapState(["user"]),
    ...mapGetters(["getOpenFilePicker"]),

    submitBtnEnabled() {
      return (
        this.songSelected &&
        this.title &&
        this.hasGenreSelected &&
        !this.saving
      );
    },
    hasGenreSelected() {
      const g = this.genreSelected;
      if (Array.isArray(g)) return g.length > 0;
      if (typeof g === "string") return g.trim().length > 0;
      return !!g;
    },
    isFileReady() {
      return !!this.songSelected;
    },
  },
  watch: {
    user() {},
    initialData: {
      handler(data) {
        if (data) {
          this.title = data.title ?? "";
          this.genreSelected = data.genreSelected ?? [];
          this.description = data.description ?? "";
        }
      },
      deep: true,
    },
    title() {
      this.saveFormData();
    },
    genreSelected() {
      this.saveFormData();
    },
    description() {
      this.saveFormData();
    },
    songSelected: {
      immediate: true,
      handler(newFile) {
        if (newFile && !this.title) {
          this.title = this.cleanFileName(newFile.name);
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";

.loading-dots {
  display: inline-block;
  min-width: 1em;
}

.field-required {
  input.error {
    border-color: $red !important;
    animation: highlight-pulse 2s ease-in-out infinite;
  }
  
  label.error {
    color: $red;
  }
}

@keyframes highlight-pulse {
  0%, 100% {
    border-color: $red;
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.4);
  }
  50% {
    border-color: $red;
    box-shadow: 0 0 0 4px rgba(255, 0, 0, 0.1);
  }
}
</style>
